// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useEffect, useMemo } from "react";
import "./SupplierInsights.scss";

import { AuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { startOfDay, endOfDay, subMonths } from "date-fns";
import { useLocalStorage } from "@uidotdev/usehooks";

// Importing custom components
import Navbar from "../../components/Navbar/Navbar";
import Sidebar from "../../components/Navbar/Sidebar";
import SupplierTable from "../../components/Suppliers/SupplierTable";
import SuppliersByMaterial from "../../components/Suppliers/SuppliersByMaterial";
import SearchBar from "../../components/Suppliers/SearchBar";
import FilterControls from "../../components/Suppliers/FilterControls";
import GroupingOptions from "../../components/Suppliers/GroupingOptions/GroupingOptions";

import {
  getSupplierDetails,
  getSupplierAggregatedData,
  filterSupplierData,
  filterSuppliersBySearchQuery,
  getSupplierNumberOfDeliveries,
  getSupplierTotalWeightTons,
  getSupplierNumberOfBalesInProduction,
} from "../../components/Suppliers/SupplierDataService";

import {
  getTableEntriesFromDeliveriesData,
  getTableEntriesFromObservationsData,
} from "../../components/Suppliers/SupplierTableDataLoader";

import { Funnel } from "lucide-react";
import { Button } from "../../components/ui/Button";
import ColumnSort from "../../components/ColumnSorter/ColumnSort";
import { SupplierInsightContextProvider } from "./SupplierInsightContext";

export default function SupplierInsights() {
  const { t } = useTranslation();
  const { currentUser, logEventCustom } = React.useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    material: "all",
    dateRange: {
      startDate: startOfDay(subMonths(new Date(), 6)),
      endDate: endOfDay(new Date()),
    },
    deliveryRange: { min: 1, max: null },
    tonnageRange: { min: null, max: null },
    balesProductionRange: { min: null, max: null },
    selectedClaimIssues: [],
  });

  // View mode (table or grouped by material)
  const [viewMode, setViewMode] = useState("all"); // 'all', 'byMaterial', or 'byRanking'

  // Data loading states
  const [suppliers, setSuppliers] = useState([]);
  const [supplierData, setSupplierData] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [sortedColumns, setSortedColumns] = useLocalStorage(
    "supplierSortedColumns",
    null
  );

  // Prefilter by material
  const prefilteredSupplierData = useMemo(() => {
    // Don't apply material filter if we're in byMaterial view
    if (viewMode === "byMaterial") return supplierData;
    return filterSupplierData({
      suppliersData: supplierData,
      materialFilter: activeFilters.material,
    });
  }, [supplierData, activeFilters.material, viewMode]);

  const maxDeliveries = useMemo(() => {
    return Object.values(prefilteredSupplierData).reduce((acc, data) => {
      const numDeliveries = getSupplierNumberOfDeliveries(data);
      return Math.max(acc, numDeliveries);
    }, 0); // Start with 0 in case supplierData is empty
  }, [prefilteredSupplierData]);

  const maxTonnage = useMemo(() => {
    return Object.values(prefilteredSupplierData).reduce((acc, data) => {
      const totalTons = getSupplierTotalWeightTons(data);
      return Math.max(acc, totalTons);
    }, 0); // Start with 0 in case supplierData is empty
  }, [prefilteredSupplierData]);

  const maxBalesInProduction = useMemo(() => {
    return Object.values(prefilteredSupplierData).reduce((acc, data) => {
      const totalBales = getSupplierNumberOfBalesInProduction(data);
      return Math.max(acc, totalBales);
    }, 0); // Start with 0 in case supplierData is empty
  }, [prefilteredSupplierData]);

  // filter supplier data based on filters
  const filterDependencies = {
    minDeliveriesFilter: activeFilters.deliveryRange.min,
    maxDeliveriesFilter: activeFilters.deliveryRange.max,
    minTonnageFilter: activeFilters.tonnageRange.min,
    maxTonnageFilter: activeFilters.tonnageRange.max,
    minBalesInProductionFilter: activeFilters.balesProductionRange.min,
    maxBalesInProductionFilter: activeFilters.balesProductionRange.max,
    selectedClaimIssuesFilter: activeFilters.selectedClaimIssues,
  };
  const supplierDataFiltered = useMemo(() => {
    return filterSupplierData({
      suppliersData: prefilteredSupplierData,
      ...filterDependencies,
    });
  }, [prefilteredSupplierData, ...Object.values(filterDependencies)]);

  // Get table entries for all suppliers
  const tableEntries = useMemo(() => {
    return {
      ...getTableEntriesFromDeliveriesData(supplierDataFiltered),
      ...getTableEntriesFromObservationsData(supplierDataFiltered),
    };
  }, [supplierDataFiltered]);

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((supplier) => supplier.id in supplierDataFiltered);
  }, [suppliers, supplierDataFiltered]);

  // Get unique materials from all suppliers
  const uniqueMaterials = useMemo(() => {
    const materialsSet = new Set();

    Object.values(supplierDataFiltered).forEach((data) => {
      if (data && data.materials && Array.isArray(data.materials)) {
        data.materials.forEach((material) => {
          if (material) materialsSet.add(material);
        });
      }
    });

    return Array.from(materialsSet).sort();
  }, [supplierDataFiltered]);

  // Handle filter changes from the FilterControls component
  function handleFilterChange(filters) {
    setActiveFilters(filters);
  }

  // Handle view mode changes from GroupingOptions
  function handleViewModeChange(mode) {
    logEventCustom("select_content", {
      content_type: "supplier_insights_grouping",
      content_id: mode,
    });
    setViewMode(mode);
  }

  // Render content based on view mode
  function renderContent() {
    switch (viewMode) {
      case "byMaterial":
        return (
          <div className="suppliers-by-material-container">
            {uniqueMaterials.length > 0 ? (
              uniqueMaterials.map((material) => (
                <SuppliersByMaterial
                  key={material}
                  material={material}
                  suppliers={filteredSuppliers}
                  supplierData={supplierData}
                  supplierDataFiltered={supplierDataFiltered}
                  tableEntries={tableEntries}
                  activeFilters={activeFilters}
                  searchQuery={searchQuery}
                  isLoading={isLoadingData}
                  sortedColumns={sortedColumns}
                />
              ))
            ) : (
              <div className="no-materials-message">
                No materials found for the current suppliers.
              </div>
            )}
          </div>
        );
      case "byRanking":
        return (
          <div className="coming-soon-message">
            <h3>Ranking Tiers View Coming Soon</h3>
            <p>This feature is currently under development.</p>
          </div>
        );
      case "all":
      default:
        return (
          <SuppliersDefaultView
            suppliers={filteredSuppliers}
            supplierData={supplierData}
            supplierDataFiltered={supplierDataFiltered}
            tableEntries={tableEntries}
            activeFilters={activeFilters}
            searchQuery={searchQuery}
            isLoadingData={isLoadingData}
            sortedColumns={sortedColumns}
          />
        );
    }
  }

  function resetColumnSort() {
    if (tableEntries?.length === 0) return;
    setSortedColumns(
      Object.entries(tableEntries).map(([columnId]) => ({
        id: columnId,
        isVisible: true,
      }))
    );
  }

  useEffect(() => {
    if (Object.keys(supplierData).length !== 0 && tableEntries) {
      if (
        sortedColumns === null ||
        sortedColumns?.length !== Object.keys(tableEntries)?.length
      ) {
        resetColumnSort();
      }
    }
  }, [tableEntries, supplierData]);

  // Load all suppliers
  useEffect(() => {
    async function fetchSuppliers() {
      if (!currentUser?.company?.company) return;

      setIsLoadingData(true);
      const suppliersData = await getSupplierDetails(
        currentUser.company.company
      );
      setSuppliers(suppliersData);
      setIsLoadingData(false);
    }
    fetchSuppliers();
  }, [currentUser?.company?.company]);

  // Load detailed data for each supplier
  useEffect(() => {
    async function fetchSuppliersData(suppliers) {
      if (!currentUser?.company?.company) return;

      setIsLoadingData(true);

      const newSupplierData = Object.fromEntries(
        await Promise.all(
          suppliers.map(async (supplier) => {
            return [
              supplier.id,
              await getSupplierAggregatedData(
                currentUser.company.company,
                supplier.id,
                activeFilters.dateRange
              ),
            ];
          })
        )
      );

      setSupplierData(newSupplierData);
      setIsLoadingData(false);
    }

    // Skip if no suppliers loaded yet
    if (!suppliers || suppliers.length === 0) return;
    fetchSuppliersData(suppliers);
  }, [
    suppliers,
    activeFilters?.dateRange?.startDate?.getTime(),
    activeFilters?.dateRange?.endDate?.getTime(),
  ]);

  return (
    <SupplierInsightContextProvider
      props={{
        allSuppliers: suppliers,
        allSupplierDataFiltered: supplierDataFiltered,
      }}
    >
      <div className="supplier-insights">
        <Sidebar />
        <div className="supplier-insights--right">
          <Navbar
            title={`${currentUser.company.company_displayname} ${t(
              "sidebar_supplier_insights"
            )}`}
          />
          <div className="supplier-insights--content">
            <div className="supplier-insights--controls">
              <GroupingOptions
                currentGroup={viewMode}
                onGroupChange={handleViewModeChange}
              />

              <div className="supplier-insights--search-and-filters">
                <SearchBar
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                />
                <div className="ml-auto flex items-center gap-3">
                  <div>
                    <ColumnSort
                      sortedColumns={sortedColumns}
                      setSortedColumns={setSortedColumns}
                      resetColumnSort={resetColumnSort}
                      tableEntries={tableEntries}
                      isLoadingData={isLoadingData}
                    ></ColumnSort>
                  </div>

                  <Button
                    className={`${
                      showFilters
                        ? "bg-blue-400 hover:bg-blue-400 text-white"
                        : ""
                    }`}
                    onClick={() => setShowFilters(!showFilters)}
                    type="button"
                    variant={"secondary"}
                  >
                    {showFilters ? t("hide_filters") : t("show_filters")}
                    <Funnel
                      size={18}
                      className={`${showFilters ? "rotated" : ""}`}
                    ></Funnel>
                  </Button>
                </div>
              </div>

              {showFilters && (
                <FilterControls
                  onFilterChange={handleFilterChange}
                  maxDeliveries={maxDeliveries}
                  maxTonnage={maxTonnage}
                  maxBalesInProduction={maxBalesInProduction}
                />
              )}
            </div>
            <div className="supplier-insights--table-container">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
    </SupplierInsightContextProvider>
  );
}

function SuppliersDefaultView({
  suppliers,
  supplierData,
  supplierDataFiltered,
  tableEntries,
  activeFilters,
  loadingSuppliers,
  searchQuery,
  isLoadingData,
  sortedColumns,
}) {
  const filteredSuppliers = useMemo(() => {
    const suppliersWithData = supplierData
      ? suppliers.filter((supplier) => supplier.id in supplierData)
      : suppliers;
    return filterSuppliersBySearchQuery(suppliersWithData, searchQuery);
  }, [suppliers, suppliers, supplierData, searchQuery]);

  return (
    <SupplierTable
      suppliers={filteredSuppliers}
      supplierData={supplierData}
      supplierDataFiltered={supplierDataFiltered}
      tableEntries={tableEntries}
      activeFilters={activeFilters}
      loadingSuppliers={loadingSuppliers}
      searchQuery={searchQuery}
      defaultSortColumn={"name"}
      defaultSortDirection={"asc"}
      defaultPageSize={25}
      showSearchInfo={true}
      isLoading={isLoadingData}
      sortedColumns={sortedColumns}
    />
  );
}
